import React, { useState } from 'react';
import {
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Tag,
  TagCloseButton,
  TagLabel,
  Wrap,
  WrapItem,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AddItemIcon } from 'components/icons';
import { graphql, useMutation } from 'utils/graphClient';
import { VaultFieldInput_deleteVaultFieldValues_Mutation } from './__generated__/VaultFieldInput_deleteVaultFieldValues_Mutation.graphql';
import { VaultFieldInput_createVaultFieldValues_Mutation } from './__generated__/VaultFieldInput_createVaultFieldValues_Mutation.graphql';
import { useFragment } from 'utils/graphClient';
import {
  VaultFieldInput_vaultManager$data,
  VaultFieldInput_vaultManager$key,
} from './__generated__/VaultFieldInput_vaultManager.graphql';

const vaultManagerFragment = graphql`
  fragment VaultFieldInput_vaultManager on VaultManager {
    id
    vaults {
      edges {
        node {
          id
          fieldValues {
            id
            field {
              id
            }
            value
          }
        }
      }
    }
  }
`;

interface VaultFieldInputProps {
  vaultId: string;
  fieldId: string;
  vaultManager: VaultFieldInput_vaultManager$key;
}

export const VaultFieldInput = ({
  vaultId,
  fieldId,
  vaultManager,
}: VaultFieldInputProps) => {
  const { vaults } = useFragment(vaultManagerFragment, vaultManager);

  const vault = vaults.edges.find((edge) => edge.node?.id === vaultId);
  const vaultFieldValues = vault?.node?.fieldValues || [];
  const fieldValues = vaultFieldValues.filter((vaultFieldValue) => {
    return vaultFieldValue.field.id === fieldId;
  });

  return (
    <Wrap align="center">
      <FieldValues vaultId={vaultId} fieldValues={fieldValues} />
      <WrapItem>
        <FieldInput vaultId={vaultId} fieldId={fieldId} />
      </WrapItem>
    </Wrap>
  );
};

const deleteVaultFieldValuesMutation = graphql`
  mutation VaultFieldInput_deleteVaultFieldValues_Mutation(
    $vaultId: ID!
    $valueIds: [ID!]!
  ) {
    deleteVaultFieldValues(input: { vaultId: $vaultId, valueIds: $valueIds }) {
      vault {
        id
        fieldValues {
          id
        }
      }
    }
  }
`;

interface FieldValuesProps {
  vaultId: string;
  fieldValues: NonNullable<
    VaultFieldInput_vaultManager$data['vaults']['edges'][0]['node']
  >['fieldValues'];
}

const FieldValues = ({ vaultId, fieldValues }: FieldValuesProps) => {
  const [deleteVaultFieldValues] =
    useMutation<VaultFieldInput_deleteVaultFieldValues_Mutation>(
      deleteVaultFieldValuesMutation,
    );

  return (
    <>
      {fieldValues.map((fieldValue) => {
        return (
          <WrapItem key={fieldValue.id}>
            <Tag size="lg" colorScheme="cyan">
              <TagLabel>{fieldValue.value}</TagLabel>
              <TagCloseButton
                onClick={() => {
                  deleteVaultFieldValues({
                    variables: {
                      vaultId,
                      valueIds: [fieldValue.id],
                    },
                  });
                }}
              />
            </Tag>
          </WrapItem>
        );
      })}
    </>
  );
};

const createVaultFieldValuesMutation = graphql`
  mutation VaultFieldInput_createVaultFieldValues_Mutation(
    $vaultId: ID!
    $values: [VaultFieldValueInput!]
  ) {
    createVaultFieldValues(input: { vaultId: $vaultId, values: $values }) {
      vault {
        id
        fieldValues {
          id
          field {
            id
          }
          value
        }
      }
    }
  }
`;

const FieldInput = ({ vaultId, fieldId }: any) => {
  const [inputValue, setInputValue] = useState('');

  const [createVaultFieldValues, isCreateVaultFieldValuesLoading] =
    useMutation<VaultFieldInput_createVaultFieldValues_Mutation>(
      createVaultFieldValuesMutation,
    );

  return (
    <InputGroup width={120} size="sm">
      <Input
        value={inputValue}
        placeholder={t`Add value`}
        isDisabled={isCreateVaultFieldValuesLoading}
        onChange={(event) => {
          setInputValue(event.target.value);
        }}
        pl={2}
        pr={8}
        borderRadius="md"
      />
      <InputRightElement>
        <IconButton
          isDisabled={!inputValue || isCreateVaultFieldValuesLoading}
          isLoading={isCreateVaultFieldValuesLoading}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          icon={<AddItemIcon />}
          onClick={() => {
            createVaultFieldValues({
              variables: {
                vaultId,
                values: [
                  {
                    fieldId,
                    value: inputValue,
                  },
                ],
              },
              onCompleted: () => {
                setInputValue('');
              },
            });
          }}
          aria-label={t`Create field value`}
        />
      </InputRightElement>
    </InputGroup>
  );
};
